import {
  type ValidationResult,
  validatePipelineConfiguration,
} from '@/queries/pipelines';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import Dropdown from 'antd/es/dropdown';
import Space from 'antd/es/space';
import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import React from 'react';
import { styled } from 'styled-components';
import { useDebounce } from 'use-debounce';

interface ValidationStatusProps {
  yamlContent: string;
  userModified: boolean;
  onValidationResult?: (result: ValidationResult | null) => void;
  bestEffortWarnings?: string[];
  onValidationStart?: () => void;
  onValidationComplete?: () => void;
}

const ValidationTag = styled(Tag)`
  margin: 0;
  min-width: 90px;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const DropdownContent = styled.div`
  background: ${({ theme }) => theme.colorBgContainer};
  border: 1px solid ${({ theme }) => theme.colorBorder};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  box-shadow: ${({ theme }) => theme.boxShadowSecondary};
  max-width: 500px;
  max-height: 400px;
  overflow-y: auto;
`;

const ValidationSection = styled.div`
  padding: ${({ theme }) => theme.paddingSM}px;
  border-bottom: 1px solid ${({ theme }) => theme.colorBorderSecondary};

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled(Typography.Text)`
  font-weight: 600;
  color: ${({ theme }) => theme.colorTextHeading};
  margin-bottom: ${({ theme }) => theme.marginXS}px;
  display: block;
`;

const ErrorItem = styled.div`
  margin: ${({ theme }) => theme.marginXS}px 0;
  padding: ${({ theme }) => theme.paddingXS}px;
  background: ${({ theme }) => theme.colorErrorBg};
  border: 1px solid ${({ theme }) => theme.colorErrorBorder};
  border-radius: ${({ theme }) => theme.borderRadiusSM}px;
`;

const WarningItem = styled.div`
  margin: ${({ theme }) => theme.marginXS}px 0;
  padding: ${({ theme }) => theme.paddingXS}px;
  background: ${({ theme }) => theme.colorWarningBg};
  border: 1px solid ${({ theme }) => theme.colorWarningBorder};
  border-radius: ${({ theme }) => theme.borderRadiusSM}px;
`;

const EmptyState = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-style: italic;
`;

export const ValidationStatus: React.FC<ValidationStatusProps> = ({
  yamlContent,
  userModified,
  onValidationResult,
  bestEffortWarnings = [],
  onValidationStart,
  onValidationComplete,
}) => {
  const [validationResult, setValidationResult] =
    React.useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = React.useState(false);
  const [hasInitiallyValidated, setHasInitiallyValidated] =
    React.useState(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  // Debounce YAML content for user changes, but allow immediate validation for initial load
  const [debouncedYamlContent] = useDebounce(
    yamlContent,
    userModified ? 1000 : 0,
  );

  // Computed property: pending validation when content differs from debounced content
  const isPendingValidation =
    userModified && yamlContent !== debouncedYamlContent;

  // Validate the debounced content when it changes
  React.useEffect(() => {
    const shouldValidate =
      userModified || (!hasInitiallyValidated && debouncedYamlContent.trim());

    if (!shouldValidate) {
      return;
    }

    if (!debouncedYamlContent.trim()) {
      setValidationResult(null);
      return;
    }

    const validateContent = async () => {
      setIsValidating(true);
      onValidationStart?.();

      try {
        const result =
          await validatePipelineConfiguration(debouncedYamlContent);
        setValidationResult(result);
        onValidationResult?.(result);
        if (!userModified) {
          setHasInitiallyValidated(true);
        }
      } catch (error) {
        const errorResult = {
          valid: false,
          errors: [
            {
              type: 'block_type_error' as const,
              blockId: 'unknown',
              blockType: 'unknown',
              errorType: 'validation_failed',
              message: 'Validation failed',
              portName: null,
              sourceBlockId: null,
              sourcePort: null,
            },
          ],
          warnings: [],
        };
        setValidationResult(errorResult);
        onValidationResult?.(errorResult);
        if (!userModified) {
          setHasInitiallyValidated(true);
        }
      } finally {
        setIsValidating(false);
        onValidationComplete?.();
      }
    };

    void validateContent();
  }, [
    debouncedYamlContent,
    userModified,
    hasInitiallyValidated,
    onValidationResult,
  ]);

  const getValidationTagProps = () => {
    if (isValidating || isPendingValidation) {
      return {
        icon: <LoadingOutlined />,
        color: 'processing' as const,
        children: 'Validating',
      };
    }

    if (!validationResult) {
      return {
        icon: <LoadingOutlined />,
        color: 'default' as const,
        children: 'No validation',
      };
    }

    if (validationResult.valid) {
      return {
        icon: <CheckCircleOutlined />,
        color: 'success' as const,
        children: 'Valid',
      };
    }

    const errorCount = validationResult.errors.length;
    return {
      icon: <CloseCircleOutlined />,
      color: 'error' as const,
      children: errorCount === 1 ? '1 Error' : `${errorCount} Errors`,
    };
  };

  const renderValidationDetails = () => {
    if (!validationResult || isValidating || isPendingValidation) {
      return (
        <DropdownContent>
          <ValidationSection>
            <EmptyState>
              {isValidating || isPendingValidation
                ? 'Validating...'
                : 'No validation results available'}
            </EmptyState>
          </ValidationSection>
        </DropdownContent>
      );
    }

    const hasErrors =
      validationResult.errors && validationResult.errors.length > 0;
    const hasValidationWarnings =
      validationResult.warnings && validationResult.warnings.length > 0;
    const hasBestEffortWarnings = bestEffortWarnings.length > 0;
    const hasWarnings = hasValidationWarnings || hasBestEffortWarnings;

    if (!hasErrors && !hasWarnings) {
      return (
        <DropdownContent>
          <ValidationSection>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <SectionTitle>✅ Configuration Valid</SectionTitle>
              <EmptyState>No errors or warnings found.</EmptyState>
            </Space>
          </ValidationSection>
        </DropdownContent>
      );
    }

    return (
      <DropdownContent>
        {hasErrors && (
          <ValidationSection>
            <SectionTitle>
              ❌ Errors ({validationResult.errors.length})
            </SectionTitle>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {validationResult.errors.map((error, index) => (
                <ErrorItem key={index}>
                  <Typography.Text
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    {error.message}
                  </Typography.Text>
                  {error.type === 'block_type_error' && (
                    <div>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: '12px' }}
                      >
                        Block: {error.blockId} ({error.blockType})
                      </Typography.Text>
                      {error.portName && (
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: '12px', marginLeft: 8 }}
                        >
                          Port: {error.portName}
                        </Typography.Text>
                      )}
                    </div>
                  )}
                  {error.type === 'connection_error' && (
                    <div>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: '12px' }}
                      >
                        Connection: {error.sourceBlockId}.{error.sourcePort} →{' '}
                        {error.targetBlockId}.{error.targetPort}
                      </Typography.Text>
                    </div>
                  )}
                </ErrorItem>
              ))}
            </Space>
          </ValidationSection>
        )}

        {hasWarnings && (
          <ValidationSection>
            <SectionTitle>
              ⚠️ Warnings (
              {(validationResult.warnings?.length || 0) +
                bestEffortWarnings.length}
              )
            </SectionTitle>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {/* Show validation warnings first */}
              {hasValidationWarnings &&
                validationResult.warnings!.map((warning, index) => (
                  <WarningItem key={`validation-${index}`}>
                    <Typography.Text
                      style={{ display: 'block', marginBottom: 4 }}
                    >
                      {warning.message}
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: '12px' }}
                    >
                      Connection: {warning.sourceBlockId}.{warning.sourcePort} →{' '}
                      {warning.targetBlockId}.{warning.targetPort}
                    </Typography.Text>
                  </WarningItem>
                ))}

              {/* Show best-effort warnings */}
              {hasBestEffortWarnings &&
                bestEffortWarnings.map((warning, index) => (
                  <WarningItem key={`best-effort-${index}`}>
                    <Typography.Text>{warning}</Typography.Text>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: '11px', display: 'block' }}
                    >
                      (Auto-recovery during parsing)
                    </Typography.Text>
                  </WarningItem>
                ))}
            </Space>
          </ValidationSection>
        )}
      </DropdownContent>
    );
  };

  const hasDetails =
    (validationResult &&
      (validationResult.errors.length > 0 ||
        validationResult.warnings.length > 0)) ||
    bestEffortWarnings.length > 0;

  // Don't show dropdown for loading states
  if (isValidating || isPendingValidation) {
    return <ValidationTag {...getValidationTagProps()} />;
  }

  // If we have no validation result but have best-effort warnings, show them
  if (!validationResult && bestEffortWarnings.length > 0) {
    return (
      <Dropdown
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        trigger={['click']}
        placement="bottomLeft"
        popupRender={() => (
          <DropdownContent>
            <ValidationSection>
              <SectionTitle>
                ⚠️ Parsing Warnings ({bestEffortWarnings.length})
              </SectionTitle>
              <Space
                direction="vertical"
                size="small"
                style={{ width: '100%' }}
              >
                {bestEffortWarnings.map((warning, index) => (
                  <WarningItem key={index}>
                    <Typography.Text>{warning}</Typography.Text>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: '11px', display: 'block' }}
                    >
                      (Auto-recovery during parsing)
                    </Typography.Text>
                  </WarningItem>
                ))}
              </Space>
            </ValidationSection>
          </DropdownContent>
        )}
      >
        <ValidationTag icon={<CloseCircleOutlined />} color="warning">
          <Space size={4}>
            {bestEffortWarnings.length === 1
              ? '1 Warning'
              : `${bestEffortWarnings.length} Warnings`}
            <DownOutlined style={{ fontSize: '10px' }} />
          </Space>
        </ValidationTag>
      </Dropdown>
    );
  }

  // No validation result and no warnings
  if (!validationResult) {
    return <ValidationTag {...getValidationTagProps()} />;
  }

  const tagProps = getValidationTagProps();

  return (
    <Dropdown
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      trigger={['click']}
      placement="bottomLeft"
      popupRender={() => renderValidationDetails()}
    >
      <ValidationTag icon={tagProps.icon} color={tagProps.color}>
        <Space size={4}>
          {tagProps.children}
          {hasDetails &&
            (dropdownOpen ? (
              <UpOutlined style={{ fontSize: '10px' }} />
            ) : (
              <DownOutlined style={{ fontSize: '10px' }} />
            ))}
        </Space>
      </ValidationTag>
    </Dropdown>
  );
};
